import React, { Component, createRef } from "react";
import { Input, Result, message, Checkbox } from "antd";
import { SmileOutlined } from "@ant-design/icons";
import { Smartphone, Monitor, Zap, CheckCircle } from "lucide-react";
import { motion } from "framer-motion";
import { connect } from "react-redux";
import {
  requestServerPost,
  selectPage,
  requestNodeServerPost,
} from "../../actions";
import PopupManager from "../../managers/PopupManager";
import CampaignSelector from "../CampaignSelector";
import DataHelper from "../../helpers/DataHelper";
import {
  useProLanguage,
  PRO_TRANSLATIONS,
} from "../../global/ProLanguageContext";

const SETUP_MODE_KEY = "cdx_screen_setup_mode";

class CreateScreenPage extends Component {
  constructor(props) {
    super(props);
    const savedMode = localStorage.getItem(SETUP_MODE_KEY) || "lite";
    this.state = {
      currentStep: 0,
      pairingcode: "",
      screenName: "",
      selectedCampaignID: -1,
      setupMode: savedMode,
      rememberMode: !!savedMode,
    };
    this.inputRef = createRef();
  }

  componentDidMount() {
    const { data } = this.props;
    if (data && data.CampaignID) {
      this.setState({ selectedCampaignID: data.CampaignID });
    }
    setTimeout(() => {
      if (this.inputRef.current) this.inputRef.current.focus();
    }, 100);
  }

  next() {
    this.setState({ currentStep: this.state.currentStep + 1 });
  }

  checkScreenAvailable = (pairingcode, callback) => {
    const { requestNodeServerPost, usertoken } = this.props;
    const params = new URLSearchParams();
    if (pairingcode.length !== 6) {
      message.error("The pairing code must be 6-digit long");
      return;
    }
    params.append("code", pairingcode);
    requestNodeServerPost(
      "checkscreenpairing",
      params,
      { "Content-Type": "application/x-www-form-urlencoded" },
      (response) => {
        if ("result" in response.data && response.data.result === 1) {
          this.setState({ pairingcode }, callback);
        } else {
          message.error(
            "No screens with the pairing code available! Please make sure your screen is connected to the internet",
          );
        }
      },
      usertoken,
    );
  };

  pairScreen = (screenname, pairingcode, screensettings, callback) => {
    const { requestNodeServerPost, usertoken } = this.props;
    const params = new URLSearchParams();
    params.append("account", screensettings.AccountCode);
    params.append("brand", screensettings.BrandCode);
    params.append("branch", screensettings.BranchCode);
    params.append("screen", screensettings.ScreenCode);
    params.append("screenname", screenname);
    params.append("code", pairingcode);
    DataHelper.clearScreenUnpariRequest(screensettings);
    setTimeout(() => {
      requestNodeServerPost(
        "attemptscreenpairing",
        params,
        { "Content-Type": "application/x-www-form-urlencoded" },
        (response) => {
          if (response.data.status === "success") callback();
          else
            message.error("The screen you are trying to pair is not available");
        },
        usertoken,
      );
    }, 500);
  };

  createScreen = () => {
    const {
      requestServerPost,
      usertoken,
      selectedBrandData,
      selectedBranchData,
    } = this.props;
    const { pairingcode, screenName, selectedCampaignID, setupMode } =
      this.state;
    const params = new FormData();
    params.append("brandid", selectedBrandData.BrandID);
    params.append("branchid", selectedBranchData.BranchID);
    params.append("screenname", screenName);
    params.append("pairingcode", pairingcode);
    if (setupMode === "full" && selectedCampaignID !== -1)
      params.append("campaignid", selectedCampaignID);
    const endpoint =
      setupMode === "lite" ? "create_screen_lite" : "create_screen";
    requestServerPost(
      endpoint,
      params,
      {},
      (response) => {
        this.pairScreen(screenName, pairingcode, response.data, () => {
          if (this.props.onSuccess) this.props.onSuccess();
          this.next();
        });
      },
      usertoken,
    );
  };

  updateScreen = () => {
    const {
      requestServerPost,
      data,
      selectedBrandData,
      selectedBranchData,
      usertoken,
    } = this.props;
    const { screenName, selectedCampaignID } = this.state;
    const params = new FormData();
    params.append("brandid", selectedBrandData.BrandID);
    params.append("branchid", selectedBranchData.BranchID);
    params.append("screenname", screenName || data.ScreenName);
    params.append("screenid", data.ScreenID);
    params.append("campaignid", selectedCampaignID);
    requestServerPost(
      "update_screen",
      params,
      {},
      () => PopupManager.closePopup(true),
      usertoken,
    );
  };

  renderStepDots(activeStep) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          gap: 6,
          marginBottom: 18,
        }}
      >
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            style={{
              width: i === activeStep ? 20 : 8,
              height: 8,
              borderRadius: 4,
              background:
                i === activeStep
                  ? "rgba(255,255,255,0.95)"
                  : "rgba(255,255,255,0.35)",
              transition: "all 0.3s",
            }}
          />
        ))}
      </div>
    );
  }

  render() {
    const { currentStep, pairingcode, screenName, setupMode, rememberMode } =
      this.state;
    const { data } = this.props;
    const t = this.props.langT || PRO_TRANSLATIONS.en.createScreenPopup;
    const otpReady = pairingcode.length === 6;
    const nameReady = screenName.trim().length > 0;

    // shared full-bleed wrapper — fills the Ant Modal body edge-to-edge
    const bleed = {
      margin: -24,
      overflow: "hidden",
    };
    // centred content column used on body sections
    const body = {
      maxWidth: 440,
      margin: "0 auto",
      padding: "32px 40px",
    };

    // ── Edit mode (launched from launchEditScreenPopup) ──────────────────
    if (data) {
      return (
        <div style={{ ...bleed }}>
          <div style={{ ...body }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                marginBottom: 24,
              }}
            >
              <div
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: 12,
                  background: "linear-gradient(135deg,#6366f1,#4f46e5)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Monitor size={22} color="#fff" />
              </div>
              <div>
                <div
                  style={{ fontSize: 17, fontWeight: 700, color: "#0f172a" }}
                >
                  {t.editTitle}
                </div>
                <div style={{ fontSize: 13, color: "#64748b" }}>
                  {data.ScreenName}
                </div>
              </div>
            </div>

            <div style={{ marginBottom: 16 }}>
              <label
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: "#64748b",
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                  display: "block",
                  marginBottom: 6,
                }}
              >
                {t.screenNameLabel}
              </label>
              <Input
                defaultValue={data.ScreenName}
                size="large"
                style={{ borderRadius: 10 }}
                onChange={(e) => this.setState({ screenName: e.target.value })}
              />
            </div>

            <div style={{ marginBottom: 28 }}>
              <label
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: "#64748b",
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                  display: "block",
                  marginBottom: 6,
                }}
              >
                {t.campaignLabel}
              </label>
              <CampaignSelector
                defaultValue={data.CampaignName}
                onSelect={(id) => this.setState({ selectedCampaignID: id })}
              />
            </div>

            <button
              onClick={this.updateScreen}
              style={{
                width: "100%",
                height: 46,
                borderRadius: 10,
                border: "none",
                background: "linear-gradient(135deg,#6366f1,#4f46e5)",
                color: "#fff",
                fontSize: 15,
                fontWeight: 600,
                cursor: "pointer",
                boxShadow: "0 2px 8px rgba(99,102,241,0.3)",
              }}
            >
              {t.saveChanges}
            </button>
          </div>
        </div>
      );
    }

    // ── Step 3: Success ──────────────────────────────────────────────────
    if (currentStep === 3) {
      return (
        <div style={{ ...bleed }}>
          <Result
            icon={
              <SmileOutlined
                style={{ color: setupMode === "lite" ? "#10b981" : "#6366f1" }}
              />
            }
            title={t.pairSuccess}
            subTitle={
              setupMode === "lite"
                ? t.pairSuccessLiteSubtitle
                : t.pairSuccessFullSubtitle
            }
            extra={
              <button
                onClick={PopupManager.closePopup}
                style={{
                  padding: "10px 28px",
                  borderRadius: 10,
                  border: "none",
                  background: "linear-gradient(135deg,#6366f1,#4f46e5)",
                  color: "#fff",
                  fontWeight: 600,
                  fontSize: 14,
                  cursor: "pointer",
                  boxShadow: "0 2px 8px rgba(99,102,241,0.32)",
                }}
              >
                {t.pairDone}
              </button>
            }
          />
        </div>
      );
    }

    // ── Step 2: Name the screen ──────────────────────────────────────────
    if (currentStep === 2) {
      return (
        <div style={{ ...bleed }}>
          <div
            style={{
              background: "linear-gradient(135deg,#6366f1 0%,#4f46e5 100%)",
              padding: "28px 40px",
              textAlign: "center",
              color: "#fff",
            }}
          >
            {this.renderStepDots(2)}
            <div
              style={{
                width: 52,
                height: 52,
                borderRadius: 14,
                background: "rgba(255,255,255,0.2)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 12px",
              }}
            >
              <Monitor size={26} color="#fff" />
            </div>
            <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 4 }}>
              {t.nameScreenTitle}
            </div>
            <div style={{ fontSize: 13, color: "rgba(255,255,255,0.75)" }}>
              {t.nameScreenSubtitle}
            </div>
          </div>

          <div style={{ ...body }}>
            <div style={{ marginBottom: setupMode === "full" ? 16 : 28 }}>
              <label
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: "#64748b",
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                  display: "block",
                  marginBottom: 6,
                }}
              >
                {t.screenNameLabel}
              </label>
              <Input
                autoFocus
                size="large"
                placeholder={t.screenNamePlaceholder}
                style={{ borderRadius: 10 }}
                value={screenName}
                onChange={(e) => this.setState({ screenName: e.target.value })}
                onPressEnter={nameReady ? this.createScreen : undefined}
              />
            </div>
            {setupMode === "full" && (
              <div style={{ marginBottom: 28 }}>
                <label
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: "#64748b",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                    display: "block",
                    marginBottom: 6,
                  }}
                >
                  {t.campaignLabel}{" "}
                  <span
                    style={{
                      color: "#94a3b8",
                      fontWeight: 400,
                      textTransform: "none",
                      letterSpacing: 0,
                    }}
                  >
                    {t.campaignOptional}
                  </span>
                </label>
                <CampaignSelector
                  onSelect={(id) => this.setState({ selectedCampaignID: id })}
                />
              </div>
            )}
            <button
              disabled={!nameReady}
              onClick={this.createScreen}
              style={{
                width: "100%",
                height: 48,
                borderRadius: 10,
                border: "none",
                background: nameReady
                  ? "linear-gradient(135deg,#10b981,#059669)"
                  : "#e2e8f0",
                color: nameReady ? "#fff" : "#94a3b8",
                fontSize: 15,
                fontWeight: 600,
                cursor: nameReady ? "pointer" : "not-allowed",
                transition: "all 0.2s",
                boxShadow: nameReady
                  ? "0 2px 10px rgba(16,185,129,0.3)"
                  : "none",
                marginBottom: 10,
              }}
            >
              {nameReady ? t.finishSetup : t.enterScreenName}
            </button>
            <button
              onClick={() => this.setState({ currentStep: 1 })}
              style={{
                width: "100%",
                height: 38,
                borderRadius: 10,
                border: "1.5px solid #e2e8f0",
                background: "transparent",
                color: "#94a3b8",
                fontSize: 13,
                fontWeight: 500,
                cursor: "pointer",
              }}
            >
              {t.back}
            </button>
          </div>
        </div>
      );
    }

    // ── Step 1: Choose setup style ───────────────────────────────────────
    if (currentStep === 1) {
      const modeCard = (
        mode,
        icon,
        title,
        bestFor,
        description,
        features,
        accentColor,
        bgColor,
        shadowColor,
      ) => {
        const isSelected = setupMode === mode;
        return (
          <div
            key={mode}
            onClick={() => {
              const next = setupMode === mode ? null : mode;
              this.setState({ setupMode: next });
              if (rememberMode) {
                if (next) localStorage.setItem(SETUP_MODE_KEY, next);
                else localStorage.removeItem(SETUP_MODE_KEY);
              }
            }}
            style={{
              border: `2px solid ${isSelected ? accentColor : "#e2e8f0"}`,
              borderRadius: 16,
              padding: "28px 20px 24px",
              cursor: "pointer",
              background: isSelected ? bgColor : "#fff",
              transition: "all 0.2s ease",
              boxShadow: isSelected
                ? `0 0 0 4px ${shadowColor}`
                : "0 1px 4px rgba(15,23,42,0.06)",
              position: "relative",
              flex: 1,
              minWidth: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
            }}
          >
            {/* Selection indicator */}
            <div
              style={{
                position: "absolute",
                top: 12,
                right: 12,
                width: 20,
                height: 20,
                borderRadius: "50%",
                border: `2px solid ${isSelected ? accentColor : "#cbd5e1"}`,
                background: isSelected ? accentColor : "transparent",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "all 0.2s",
              }}
            >
              {isSelected && <CheckCircle size={12} color="#fff" />}
            </div>
            {/* Icon */}
            <div
              style={{
                width: 52,
                height: 52,
                borderRadius: 14,
                background: isSelected ? accentColor : "#f1f5f9",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginBottom: 14,
                transition: "all 0.2s",
              }}
            >
              {React.cloneElement(icon, {
                size: 24,
                color: isSelected ? "#fff" : "#64748b",
              })}
            </div>
            {/* Title */}
            <div
              style={{
                fontSize: 15,
                fontWeight: 700,
                color: "#0f172a",
                marginBottom: 4,
              }}
            >
              {title}
            </div>
            {/* Best for — badge, fixed height so features align across cards */}
            <div
              style={{
                minHeight: 28,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginBottom: 14,
              }}
            >
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: accentColor,
                  background: isSelected ? shadowColor : `${accentColor}14`,
                  border: `1.5px solid ${isSelected ? accentColor : `${accentColor}40`}`,
                  borderRadius: 20,
                  padding: "3px 10px",
                  lineHeight: 1.5,
                  transition: "all 0.2s",
                  textAlign: "center",
                }}
              >
                {bestFor}
              </span>
            </div>
            {/* Features */}
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 6,
                width: "100%",
              }}
            >
              {features.map((f, i) => (
                <div
                  key={i}
                  style={{
                    fontSize: 12,
                    fontWeight: 500,
                    color: isSelected ? accentColor : "#94a3b8",
                    background: isSelected
                      ? shadowColor
                      : "rgba(148,163,184,0.08)",
                    borderRadius: 8,
                    padding: "5px 10px",
                    transition: "all 0.2s",
                  }}
                >
                  {f}
                </div>
              ))}
            </div>
          </div>
        );
      };

      return (
        <div style={{ ...bleed }}>
          <div
            style={{
              background: "linear-gradient(135deg,#6366f1 0%,#4f46e5 100%)",
              padding: "28px 40px",
              textAlign: "center",
              color: "#fff",
            }}
          >
            {this.renderStepDots(1)}
            <div
              style={{
                width: 52,
                height: 52,
                borderRadius: 14,
                background: "rgba(255,255,255,0.2)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 12px",
              }}
            >
              <Zap size={26} color="#fff" />
            </div>
            <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 4 }}>
              {t.setupTitle}
            </div>
            <div style={{ fontSize: 13, color: "rgba(255,255,255,0.75)" }}>
              {t.setupSubtitle}
            </div>
          </div>

          <div style={{ ...body, maxWidth: 580 }}>
            <div style={{ display: "flex", gap: 16, marginBottom: 20 }}>
              {modeCard(
                "lite",
                <Zap />,
                t.setupLiteTitle,
                t.setupLiteBestFor,
                t.setupLiteDesc,
                [t.setupLiteFeature1, t.setupLiteFeature2, t.setupLiteFeature3],
                "#10b981",
                "#f0fdf4",
                "rgba(16,185,129,0.12)",
              )}
              {modeCard(
                "full",
                <Monitor />,
                t.setupFullTitle,
                t.setupFullBestFor,
                t.setupFullDesc,
                [t.setupFullFeature1, t.setupFullFeature2, t.setupFullFeature3],
                "#6366f1",
                "#f5f3ff",
                "rgba(99,102,241,0.12)",
              )}
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 16,
              }}
            >
              <Checkbox
                checked={rememberMode}
                onChange={(e) => {
                  const checked = e.target.checked;
                  this.setState({ rememberMode: checked });
                  if (checked && setupMode)
                    localStorage.setItem(SETUP_MODE_KEY, setupMode);
                  else localStorage.removeItem(SETUP_MODE_KEY);
                }}
              >
                <span style={{ fontSize: 12, color: "#64748b" }}>
                  {t.setupRemember}
                </span>
              </Checkbox>
              {rememberMode && setupMode && (
                <span
                  onClick={() => {
                    this.setState({ setupMode: null, rememberMode: false });
                    localStorage.removeItem(SETUP_MODE_KEY);
                  }}
                  style={{
                    fontSize: 11,
                    color: "#94a3b8",
                    cursor: "pointer",
                    textDecoration: "underline",
                  }}
                >
                  {t.setupClear}
                </span>
              )}
            </div>
            <button
              disabled={!setupMode}
              onClick={() => this.next()}
              style={{
                marginTop: 4,
                width: "100%",
                height: 48,
                borderRadius: 10,
                border: "none",
                background: setupMode
                  ? "linear-gradient(135deg,#6366f1,#4f46e5)"
                  : "#e2e8f0",
                color: setupMode ? "#fff" : "#94a3b8",
                fontSize: 15,
                fontWeight: 600,
                cursor: setupMode ? "pointer" : "not-allowed",
                transition: "all 0.2s",
                boxShadow: setupMode
                  ? "0 2px 10px rgba(99,102,241,0.3)"
                  : "none",
                marginBottom: 10,
              }}
            >
              {setupMode ? t.setupContinue : t.setupSelectStyle}
            </button>
            <button
              onClick={() => this.setState({ currentStep: 0, setupMode: null })}
              style={{
                width: "100%",
                height: 38,
                borderRadius: 10,
                border: "1.5px solid #e2e8f0",
                background: "transparent",
                color: "#94a3b8",
                fontSize: 13,
                fontWeight: 500,
                cursor: "pointer",
              }}
            >
              {t.back}
            </button>
          </div>
        </div>
      );
    }

    // ── Step 0: OTP entry ────────────────────────────────────────────────
    return (
      <div style={{ ...bleed }}>
        {/* Full-width gradient header */}
        <div
          style={{
            background: "linear-gradient(135deg,#6366f1 0%,#4f46e5 100%)",
            padding: "36px 40px",
            textAlign: "center",
            color: "#fff",
          }}
        >
          {this.renderStepDots(0)}
          <motion.div
            animate={{ y: [0, -8, 0] }}
            transition={{ repeat: Infinity, duration: 3, ease: "easeInOut" }}
          >
            <Smartphone
              size={56}
              color="#ffffff"
              style={{ marginBottom: 14, opacity: 0.9 }}
            />
          </motion.div>
          <div
            style={{
              fontSize: 22,
              fontWeight: 700,
              color: "#fff",
              marginBottom: 6,
            }}
          >
            {t.pairTitle}
          </div>
          <div style={{ fontSize: 14, color: "rgba(255,255,255,0.75)" }}>
            {t.pairSubtitle}
          </div>
        </div>

        <div style={{ ...body, textAlign: "center" }}>
          <div
            style={{
              position: "relative",
              display: "flex",
              justifyContent: "center",
              gap: 8,
              marginBottom: 28,
            }}
          >
            <Input
              ref={this.inputRef}
              autoFocus
              value={pairingcode}
              onChange={(e) =>
                this.setState({
                  pairingcode: e.target.value
                    .replace(/[^A-Za-z0-9]/g, "")
                    .slice(0, 6),
                })
              }
              onPressEnter={
                otpReady
                  ? () =>
                      this.checkScreenAvailable(pairingcode, () => this.next())
                  : undefined
              }
              maxLength={6}
              style={{
                position: "absolute",
                opacity: 0,
                width: "100%",
                height: "100%",
                cursor: "text",
                zIndex: 10,
                top: 0,
                left: 0,
              }}
            />
            {[...Array(6)].map((_, i) => (
              <div
                key={i}
                style={{
                  width: 44,
                  height: 54,
                  borderRadius: 12,
                  border: `2px solid ${pairingcode.length === i ? "#6366f1" : pairingcode.length > i ? "#10b981" : "#e2e8f0"}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 22,
                  fontWeight: 700,
                  color: "#0f172a",
                  background: pairingcode.length === i ? "#f5f3ff" : "#ffffff",
                  transition: "all 0.2s ease",
                  boxShadow:
                    pairingcode.length === i
                      ? "0 0 0 4px rgba(99,102,241,0.1)"
                      : "none",
                  flexShrink: 0,
                }}
              >
                {pairingcode[i] || ""}
              </div>
            ))}
          </div>

          <button
            disabled={!otpReady}
            onClick={() =>
              this.checkScreenAvailable(pairingcode, () => this.next())
            }
            style={{
              width: "100%",
              height: 48,
              borderRadius: 10,
              border: "none",
              background: otpReady
                ? "linear-gradient(135deg,#10b981,#059669)"
                : "#e2e8f0",
              color: otpReady ? "#fff" : "#94a3b8",
              fontSize: 15,
              fontWeight: 600,
              cursor: otpReady ? "pointer" : "not-allowed",
              transition: "all 0.2s",
              boxShadow: otpReady ? "0 2px 10px rgba(16,185,129,0.3)" : "none",
            }}
          >
            {otpReady ? t.pairContinue : t.pairEnterCode}
          </button>
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  userdata: state.userdata,
  usertoken: state.usertoken,
  selectedBrandData: state.selectedBrandData,
  selectedBranchData: state.selectedBranchData,
});

const ConnectedCreateScreenPage = connect(mapStateToProps, {
  requestServerPost,
  selectPage,
  requestNodeServerPost,
})(CreateScreenPage);

const CreateScreenPageWithLang = (props) => {
  const { lang } = useProLanguage();
  const t =
    PRO_TRANSLATIONS[lang]?.createScreenPopup ||
    PRO_TRANSLATIONS.en.createScreenPopup;
  return <ConnectedCreateScreenPage langT={t} {...props} />;
};

export default CreateScreenPageWithLang;
